/**
 * coloringSchemaEngine.js
 *
 * ENGINE half of the coloring-schema module (see .claude/features/COLORING_SCHEMA_RESEARCH.md
 * §4.4, §4.6, §4.8, §4.10). Compiles a widget's `schemaSelection` into a memoized, hot-path
 * lookup function that maps a measure to a matched tone/color OR one of the four failure
 * render states — never a wrong color.
 *
 * Depends on the DEFINITIONS half (`coloringSchemas.js`) for the enums, the built-in registry
 * and the canonical `validateSchema`; it imports from that file only (one direction, no cycle).
 * Number matching reuses the proven `bucketOf` matcher (`@/utils/colorBuckets`, §4.2) rather
 * than inventing a second bucket engine. PURE module — no Vuex, no components; band-backed
 * schemas receive the facility's live bands via `context.bands`.
 */

import { bucketOf } from '@/utils/colorBuckets';
import { NO_DATA_COLOR } from '@/features/Tags/components/monitoring/cmlColoringConstants';
import { log as debugLog, error as debugError } from '@/utils/debugLogger';
import { convertUnit, canConvert, normalizeUnit } from './unitConversion';
import { textSafeColor } from './textSafeColor';
import {
  APPLIES_TO,
  RENDER_STATE,
  SCHEMA_KIND,
  THEME,
  getBuiltInSchema,
  validateSchema,
} from './coloringSchemas';

// ─── Outcome builders (§4.4, §4.9) ──────────────────────────────────────────────
// Every MATCHED outcome colors the KPI value TEXT (chips are reserved for FAILURE states only,
// §4.9), via one of two mechanisms the consumer selects:
//   • tone match      → `tone` set, `canColorText: true`, `textColor: null` — apply the shipped
//                       semantic tone class.
//   • raw-color match → `tone: null`, `canColorText: false`, `textColor` = a contrast-adjusted
//                       (>= 4.5:1) hex to paint on the value text directly.
// So `textColor != null` ⇔ raw-color match, and `canColorText` now merely SELECTS the mechanism
// (tone class vs textColor string) — it NO LONGER means "text coloring suppressed". `resolvedColor`
// stays the RAW hue for chart fills / wizard swatches (3:1 + label); NEVER use it for value text.
// Neutral / failure states carry `textColor: null` + `canColorText: false` → consumer uses ink.
// Consumer contract (3-way): textColor → paint text that color; else tone → tone class; else ink.

function pickHex(pair, theme) {
  return theme === THEME.DARK ? pair.dark : pair.light;
}

function matchedTone(tone, label) {
  return { state: RENDER_STATE.MATCHED, tone, color: null, resolvedColor: null, textColor: null, label, canColorText: true };
}

function matchedColor(pair, label, theme) {
  const resolvedColor = pickHex(pair, theme);
  return {
    state: RENDER_STATE.MATCHED,
    tone: null,
    color: pair,
    resolvedColor, // RAW hue — chart fills / wizard swatches (3:1 + label)
    textColor: textSafeColor(resolvedColor, theme), // contrast-adjusted (>= 4.5:1) — KPI value TEXT only
    label,
    canColorText: false,
  };
}

function neutralOutcome(state, label) {
  return { state, tone: null, color: null, resolvedColor: NO_DATA_COLOR, textColor: null, label, canColorText: false };
}

// Frozen singletons for the invariant schema-level failures (no per-lookup allocation).
const UNAVAILABLE_OUTCOME = Object.freeze(neutralOutcome(RENDER_STATE.SCHEMA_UNAVAILABLE, 'Schema unavailable'));
const LOAD_ERROR_OUTCOME = Object.freeze(neutralOutcome(RENDER_STATE.LOAD_ERROR, 'Colors unavailable'));

// An entry/range/band → a matched outcome. Store bands (judgCor hex + title) always paint a
// raw chip; authored entries/ranges use their tone or color.
function paintOutcome(item, theme) {
  if (item.judgCor) {
    const pair = { light: item.judgCor, dark: item.judgCor };
    return matchedColor(pair, item.title || item.judgSymb || '', theme);
  }
  if (item.tone != null) return matchedTone(item.tone, item.label || '');
  return matchedColor(item.color, item.label || '', theme);
}

// Turn an optional `fallback` into a COLORED catch-all outcome, or null when it only carries
// a label (a colorless fallback falls through to the labeled unmappedValue state — §4.4).
function fallbackOutcome(fallback, theme) {
  if (!fallback) return null;
  if (fallback.tone != null) return matchedTone(fallback.tone, fallback.label || 'Other');
  if (fallback.color != null) return matchedColor(fallback.color, fallback.label || 'Other', theme);
  return null;
}

// ─── Compilation → hot-path lookup (§4.8, §4.10) ────────────────────────────────

function constantLookup(outcome) {
  return () => outcome;
}

// Map canonical half-open [from, to) → a bucketOf option: lower inclusive (>=), upper
// exclusive (<), null ends → ±∞ sentinels the matcher understands.
function rangeToOption(range) {
  return {
    judgMkr1: '<', judgCrr1: range.to == null ? '∞' : range.to,
    judgMkr2: range.from == null ? '>' : '>=', judgCrr2: range.from == null ? '-∞' : range.from,
  };
}

// Build a value→outcome matcher over a set of bucketOf options with parallel outcomes,
// resolved by object identity so bucketOf's first-match contract is preserved.
function makeRangeMatcher(options, outcomes) {
  const byOption = new Map();
  options.forEach((option, i) => byOption.set(option, outcomes[i]));
  return (value) => {
    const hit = bucketOf(value, options);
    return hit ? byOption.get(hit) : null;
  };
}

function compileCategoryLookup(schema, theme) {
  const noData = neutralOutcome(RENDER_STATE.NO_DATA, (schema.noData && schema.noData.label) || 'No data');
  const byKey = new Map();
  (schema.entries || []).forEach((entry) => {
    byKey.set(String(entry.value).trim().toLowerCase(), paintOutcome(entry, theme));
  });
  const colored = fallbackOutcome(schema.fallback, theme);
  const unmapped = neutralOutcome(RENDER_STATE.UNMAPPED_VALUE, (schema.fallback && schema.fallback.label) || 'Other');
  return (value) => {
    if (value == null) return noData;
    const key = String(value).trim().toLowerCase();
    if (key === '') return noData;
    const hit = byKey.get(key);
    if (hit) return hit;
    return colored || unmapped;
  };
}

function compileNumberLookup(schema, options, outcomes, selection, theme) {
  const declaredUnit = (selection && selection.valueUnit) || schema.unit;
  // An UNKNOWN unit (not in the convert registry) is a free-text, display-only label → treat the
  // schema as DIMENSIONLESS and bucket the RAW value (no conversion), rather than greying the whole
  // widget. `dimensionless` is true when EITHER the schema unit or the effective bound unit is not
  // convert-known. Only a pair of KNOWN, cross-dimension units (e.g. mm vs yr) is the genuine hard
  // failure. Decided ONCE here (off the hot path) — the bound unit is fixed per compiled lookup.
  const schemaUnitKnown = normalizeUnit(schema.unit) != null;
  const dimensionless = !schemaUnitKnown || normalizeUnit(declaredUnit) == null;
  if (dimensionless) {
    debugLog('[coloringSchemaEngine] unknown unit — bucketing raw values (dimensionless)',
      !schemaUnitKnown ? `schema.unit="${schema.unit}"` : `valueUnit="${declaredUnit}"`, `(schema ${schema.id})`);
  } else if (selection && selection.valueUnit && !canConvert(selection.valueUnit, schema.unit)) {
    // Both units known but cross-dimension — a real mismatch; grey out (preserved, §4.6).
    debugError('[coloringSchemaEngine] unit unconvertible', selection.valueUnit, '->', schema.unit, `(schema ${schema.id})`);
    return constantLookup(UNAVAILABLE_OUTCOME);
  }
  const noData = neutralOutcome(RENDER_STATE.NO_DATA, (schema.noData && schema.noData.label) || 'No data');
  const colored = fallbackOutcome(schema.fallback, theme);
  const unmapped = neutralOutcome(RENDER_STATE.UNMAPPED_VALUE, (schema.fallback && schema.fallback.label) || 'Out of range');
  const match = makeRangeMatcher(options, outcomes);
  return (value, opts) => {
    if (value == null) return noData;
    const num = Number(value);
    if (Number.isNaN(num)) return noData;
    // Dimensionless → bucket raw; otherwise convert the (known) bound unit into the (known) schema unit.
    const converted = dimensionless ? num : convertUnit(num, (opts && opts.valueUnit) || declaredUnit, schema.unit);
    if (converted == null) return UNAVAILABLE_OUTCOME;
    const hit = match(converted);
    if (hit) return hit;
    return colored || unmapped;
  };
}

// Resolve a schemaSelection to its definition. builtin → code registry; inline → the embedded
// object; reference → the matching schema in `librarySchemas` (the dashboard's saved schemas).
// `kind` is the NAMESPACE: a reference id resolves ONLY from librarySchemas and a builtin id ONLY
// from the code registry, so the same id string under two kinds is two independent schemas — a
// reference can never shadow (or be shadowed by) a built-in.
//
// Exported so chart-side consumers (echartsOptions.js) share ONE reference-resolution path rather
// than re-implementing the builtin/inline branches and dropping `reference` (the "schemas only
// work on KPIs" bug): any surface that needs a selection's raw definition — its `appliesTo`, its
// `ranges` — resolves it here, threading the same `librarySchemas`.
export function resolveDefinition(selection, librarySchemas) {
  if (!selection || typeof selection !== 'object') return null;
  if (selection.kind === SCHEMA_KIND.INLINE) return selection.definition || null;
  if (selection.kind === SCHEMA_KIND.BUILTIN) return getBuiltInSchema(selection.schemaId);
  if (selection.kind === SCHEMA_KIND.REFERENCE) {
    // followLatest is always true in Phase 1/2 (version pinning is NOT implemented) — resolve the
    // current library content by id. Duplicate ids take first-match (uuid uniqueness is the data
    // contract). Absent librarySchemas (e.g. the async config load has not landed) → null →
    // schemaUnavailable; the consumer must gate reference rendering on "config loaded" (§4.5 seam).
    if (!Array.isArray(librarySchemas)) return null;
    return librarySchemas.find((s) => s && s.id === selection.schemaId) || null;
  }
  return null; // unknown kind
}

function compileLookup(selection, context) {
  const schema = resolveDefinition(selection, context.librarySchemas);
  if (!schema) {
    debugLog('[coloringSchemaEngine] schema unavailable — no definition', selection && selection.kind, selection && selection.schemaId);
    return constantLookup(UNAVAILABLE_OUTCOME);
  }
  // Validate anything not code-shipped; a malformed stored blob must not crash the render.
  if (selection.kind !== SCHEMA_KIND.BUILTIN) {
    const { valid } = validateSchema(schema);
    if (!valid) return constantLookup(UNAVAILABLE_OUTCOME);
  }
  if (schema.appliesTo === APPLIES_TO.CATEGORY) return compileCategoryLookup(schema, context.theme);

  // Number schema. Facility-injected bands are a RETAINED Phase-2 capability (no Phase-1 user):
  // context.bands is the live facility set (store shape — already bucketOf options), read without
  // mutation; its absence/error surfaces the loadError / schemaUnavailable states.
  if (schema.bandBacked) {
    if (context.bandsError) {
      debugLog('[coloringSchemaEngine] load error — facility bands failed to load', schema.id, context.facility);
      return constantLookup(LOAD_ERROR_OUTCOME);
    }
    const bands = context.bands;
    if (!Array.isArray(bands) || bands.length === 0) {
      debugLog('[coloringSchemaEngine] schema unavailable — no facility bands', schema.id, context.facility);
      return constantLookup(UNAVAILABLE_OUTCOME);
    }
    const outcomes = bands.map((band) => paintOutcome(band, context.theme));
    return compileNumberLookup(schema, bands, outcomes, selection, context.theme);
  }
  // Authored number schema with canonical half-open ranges — BOTH the Phase-1 built-in
  // default-band schemas (converted from the fixed defaults at module load) AND inline custom
  // schemas take this path, resolving with NO injected context.
  const ranges = schema.ranges || [];
  const options = ranges.map(rangeToOption);
  const outcomes = ranges.map((range) => paintOutcome(range, context.theme));
  return compileNumberLookup(schema, options, outcomes, selection, context.theme);
}

// ─── Memoized resolveSchema (§4.8) ──────────────────────────────────────────────

const resolveCache = new Map();

// Deterministic key for an inline definition so structurally identical blobs share a
// compiled lookup (sorted-key stringify; safe for the small Phase 1.5 shapes).
function stableStringify(value) {
  if (value == null || typeof value !== 'object') return JSON.stringify(value) || 'null';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const keys = Object.keys(value).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(',')}}`;
}

function selectionKeyPart(selection, librarySchemas) {
  if (!selection || typeof selection !== 'object') return 'invalid';
  // valueUnit is baked into a number lookup (declaredUnit + the compile-time unit gate),
  // so two bindings that differ only by unit must NOT share a compiled lookup.
  const unit = selection.valueUnit ? `|u:${selection.valueUnit}` : '';
  if (selection.kind === SCHEMA_KIND.INLINE) return `inline:${stableStringify(selection.definition || null)}${unit}`;
  if (selection.kind === SCHEMA_KIND.REFERENCE) {
    // Key on the RESOLVED library schema's CANONICAL CONTENT (stableStringify — the actual content,
    // NOT a lossy digest; do not "optimize" into a fixed-width hash). So a manager edit recolors
    // consumers on the next resolve with no caller-passed version, and a fresh-but-equal
    // librarySchemas array (Vue re-render / re-fetch) is a cache HIT. Missing id → `null` → a stable
    // schemaUnavailable entry. The extra find here (compileLookup resolves again) is cheap.
    return `reference:${selection.schemaId || ''}:${stableStringify(resolveDefinition(selection, librarySchemas))}${unit}`;
  }
  return `${selection.kind}:${selection.schemaId || ''}${unit}`;
}

/**
 * Compile (or reuse) a hot-path lookup for a schemaSelection. Memoized on
 * `selection (id or inline hash) + facility + bandsVersion + theme` (§4.8): a widget refresh
 * neither rebuilds the matcher nor refetches bands, and a bands-version bump (from the
 * facility bands loader) invalidates the entry when a facility's bands change.
 *
 * The returned lookup is `lookup(value, opts?) → outcome`, where `outcome` is
 * `{ state, tone, color, resolvedColor, textColor, label, canColorText }` (`textColor` is a
 * contrast-adjusted hex on a raw-color match, else null — see the module header). Number lookups accept
 * `opts.valueUnit` (the unit the measure is in) and convert into the schema unit before
 * bucketing; an unconvertible unit yields `schemaUnavailable`, never a mis-bucket (§4.6).
 *
 * A `reference` selection resolves its schema from `context.librarySchemas` (the dashboard's saved
 * schemas); the key includes that schema's canonical content so a manager edit recolors consumers
 * on the next resolve. An absent library (async config not yet loaded) or a missing id yields
 * `schemaUnavailable` — the consumer gates reference rendering on "config loaded" (§4.5 seam).
 *
 * @param {object} selection - { kind, schemaId?, definition?, valueUnit? }
 * @param {object} [context] - { facility?, bands?, bandsError?, bandsVersion?, theme?, librarySchemas? }
 * @returns {(value: *, opts?: { valueUnit?: string }) => object}
 */
export function resolveSchema(selection, context = {}) {
  const {
    facility = null,
    bands = null,
    bandsError = false,
    bandsVersion = 0,
    theme = THEME.LIGHT,
    librarySchemas = null,
  } = context;
  // The doc's key is id/hash + facility + bands-version + theme; `bandsError` is added
  // because it flips the compiled outcome (loadError vs schemaUnavailable) without
  // necessarily bumping bands-version, so it must not alias a non-error entry. The
  // facility/bands-version/bandsError components are INERT for the Phase-1 static built-ins
  // (they consume no injected context) but are retained because the engine keeps the Phase-2
  // bands-injection path, for which they remain load-bearing — stripping them would
  // reintroduce the cross-facility/error cache collisions this key guards against. For a
  // `reference`, selectionKeyPart folds in the resolved library schema's content (self-invalidating).
  const key = `${selectionKeyPart(selection, librarySchemas)}::${facility || ''}::${bandsVersion || 0}::${bandsError ? 'err' : 'ok'}::${theme}`;
  const cached = resolveCache.get(key);
  if (cached) return cached;
  const lookup = compileLookup(selection, { facility, bands, bandsError, theme, librarySchemas });
  resolveCache.set(key, lookup);
  return lookup;
}

/** Drop all memoized lookups (tests, or a global bands refresh). */
export function clearResolveCache() {
  resolveCache.clear();
}
